import { useEffect, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { BoardView, ListView, type MoveHandler } from "../components/board"
import { TaskCreateModal } from "../components/TaskCreateModal"
import { BoardConfigProvider, DEFAULT_CONFIG, prioritiesOf, useBoardConfig } from "../lib/boardConfig"
import { readBoardSort, sortBoardTasks, sortTaskTree, type BoardSort } from "../lib/boardSort"
import {
  Board as BoardIcon,
  ChevronDown,
  List as ListIcon,
  Plus,
  PriorityIcon,
  LevelIcon,
  Search,
  Sort as SortIcon,
  TypeIcon,
} from "../components/icons"
import {
  Button,
  Dropdown,
  Empty,
  ErrorNote,
  InlineEdit,
  type MenuItem,
  SegmentedControl,
  Skeleton,
  formatDuration,
  useLoad,
  useToast,
} from "../components/ui"
import { useRoster } from "./work"
import {
  type AssigneeFilterOption,
  type ProjectAssignee,
  assigneeFilterOptions,
} from "../lib/assignees"
import {
  type Project,
  type Sprint,
  type Status,
  type Task,
  type TaskLevel,
  api,
} from "../lib/api"
import { useSession } from "../lib/auth"
import { LEVELS, LEVEL_LABELS } from "../lib/levels"
// One project: its tasks as a board or a list, with the filters in the URL so a
// filtered view can be pasted to a colleague.

// The shell renders this route full-bleed (see layout.tsx), so every state of
// the page — loading and not-found included — brings its own padding. No width
// cap: a board is as wide as its columns, and a wide screen should show more of
// it rather than a centred box.
const PAGE = "flex h-full min-h-0 flex-col gap-4 px-4 py-4 sm:px-6"

export function ProjectDetailPage() {
  const navigate = useNavigate()
  const { projectId = "" } = useParams()
  const [params, setParams] = useSearchParams()
  const { employee } = useSession()
  const { assignable } = useRoster()
  const toast = useToast()
  const [creating, setCreating] = useState<TaskLevel | null>(null)

  const rawView = params.get("view")
  const view = rawView === "list" ? rawView : "board"
  // Newest last by default, priority when it is asked for; see lib/boardSort.
  const sort = readBoardSort(params.get("sort"))
  // Swimlanes by epic, as on a YouTrack agile board. Every level is a card
  // either way; the lanes only group them.
  // Everything except `view`, `group` and `sort`, which are display
  // preferences rather than filters and must not be sent to the API as
  // themselves.
  const query = new URLSearchParams(params)
  query.delete("view")
  query.delete("group")
  query.delete("sort")
  const apiQuery = query
  // What narrows the board, for epic lanes that open to show its matches: set
  // filters only — an empty `q=` narrows nothing, and archived tasks widen it.
  const filterKey = new URLSearchParams([...query].filter(([key, value]) => value && key !== "includeArchived")).toString()

  const project = useLoad(() => api.get<{ project: Project }>(`/projects/${projectId}`), [projectId])
  const sprints = useLoad(
    () => api.get<{ sprints: Sprint[] }>(`/projects/${projectId}/sprints`),
    [projectId],
  )
  // Who owns this project's tasks, archived included. The filter needs it
  // because the loaded tasks are already filtered: "what did Bob own?" is a
  // question about history, and Bob may be disabled now.
  const projectAssignees = useLoad(
    () => api.get<{ assignees: ProjectAssignee[] }>(`/projects/${projectId}/assignees`),
    [projectId],
  )
  // The project's epics, for the epic filter — whatever the board is filtered
  // to, the list of epics to pick from stays whole.
  const epics = useLoad(
    () => api.get<{ tasks: Task[] }>(`/projects/${projectId}/tasks?level=epic`),
    [projectId],
  )
  // Opt-in: the board opens as plain columns, and lanes appear only once the
  // user asks for them (then folded, see lib/laneFolds). An old `group=none`
  // link still reads as off.
  const swimlanes = params.get("group") === "epic"
  const loaded = useLoad(
    () =>
      api.get<{ tasks: Task[]; minutesLogged: number }>(
        `/projects/${projectId}/tasks${apiQuery.size ? `?${apiQuery}` : ""}`,
      ),
    [projectId, apiQuery.toString()],
  )

  // A local copy so a drag can land immediately. The fetch is the source of
  // truth and overwrites it whenever it returns.
  const [tasks, setTasks] = useState<Task[]>([])
  useEffect(() => setTasks(loaded.data?.tasks ?? []), [loaded.data])

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const onMove: MoveHandler = async (taskId, status, index) => {
    const before = tasks
    const moving = before.find((t) => t.id === taskId)
    if (!moving) return
    // Dropping a card back exactly where it came from is not a move; without
    // this every click-through of the status menu writes statusChangedAt.
    const column = before.filter((t) => t.status === status && t.id !== taskId)
    if (moving.status === status && column.indexOf(moving) === index) return

    setTasks(applyMove(before, taskId, status, index))
    try {
      await api.post(`/projects/tasks/${taskId}/reorder`, { status, position: index })
      // Re-read rather than trusting the local splice: the server renumbers the
      // whole column, and another person may have moved something in it.
      loaded.reload()
    } catch (error) {
      setTasks(before)
      toast(error instanceof Error ? error.message : "Could not move that task", "error")
    }
  }

  if (project.loading)
    return (
      <div className={PAGE}>
        <Skeleton className="h-40" />
      </div>
    )
  if (project.error)
    return (
      <div className={PAGE}>
        <ErrorNote error={project.error} />
      </div>
    )
  const p = project.data?.project
  if (!p)
    return (
      <div className={PAGE}>
        <Empty title="Project not found" />
      </div>
    )

  const labelsInUse = [...new Set(tasks.flatMap((t) => t.labels))].sort()
  const activeFilters = [...query.keys()].filter((k) => query.get(k))
  // The columns read one flat order; the list keeps children under their
  // parents and sorts only siblings. Manual passes the loaded order through.
  const config = p.config ?? DEFAULT_CONFIG
  const shown = view === "list"
    ? sortTaskTree(tasks, sort, config)
    : sortBoardTasks(tasks, sort, config)

  return (
    // Everything inside — columns, pills, pickers — reads this board's own
    // statuses and types from context instead of the shipped defaults.
    //
    // With no outer scroll the page owns its height: the header stays fixed at
    // the top and only the view below scrolls — the board as one shared grid,
    // the list in the box below (MXX-99, Bug 2).
    <BoardConfigProvider value={p.config ?? DEFAULT_CONFIG}>
      <div className={PAGE}>
        <header className="flex shrink-0 flex-col gap-3 border-b border-border pb-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex items-center gap-2 text-xs text-faint">
                <Link to="/projects" className="hover:text-muted hover:underline">
                  Projects
                </Link>
                <span>/</span>
                <span className="font-mono">{p.keyPrefix ?? "—"}</span>
              </div>
              <InlineEdit
                ariaLabel="Project name"
                value={p.name}
                className="text-xl font-medium tracking-tight text-foreground"
                inputClassName="text-xl font-medium tracking-tight text-foreground"
                onSave={async (name) => {
                  if (!name) return
                  await api.patch(`/projects/${p.id}`, { name })
                  toast("Project renamed")
                  project.reload()
                }}
              />
              <InlineEdit
                ariaLabel="Project description"
                value={p.description ?? ""}
                placeholder="Add a description"
                className="max-w-prose text-sm text-muted"
                inputClassName="text-sm text-muted"
                multiline
                onSave={async (description) => {
                  await api.patch(`/projects/${p.id}`, { description })
                  project.reload()
                }}
              />
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {/* The full query language — descriptions, comments, field
                  filters — on its own page. The board's box above stays the
                  quick key-or-title filter. The board's text carries over. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/projects/${p.id}/issues${params.get("q") ? `?q=${encodeURIComponent(params.get("q")!)}` : ""}`)}
              >
                <Search className="size-3.5" /> Search
              </Button>
              <Dropdown
                items={[
                  { label: "New epic", onSelect: () => setCreating("epic") },
                  { label: "New task", onSelect: () => setCreating("task") },
                ]}
                trigger={({ toggle }) => <Button size="sm" onClick={toggle}><Plus /> New item <ChevronDown className="size-3" /></Button>}
              />
              {!!loaded.data?.minutesLogged && (
                <span
                  className="font-mono text-xs text-faint tabular-nums"
                  title="Time logged by people on this project"
                >
                  Logged {formatDuration(loaded.data.minutesLogged)}
                </span>
              )}
              <ProjectMenu project={p} onChange={project.reload} exportQuery={apiQuery} />
            </div>
          </div>

          <Toolbar
            params={params}
            setParam={setParam}
            view={view}
            sort={sort}
            labelsInUse={labelsInUse}
            assigneeOptions={assigneeFilterOptions(
              employee.id,
              assignable,
              projectAssignees.data?.assignees ?? [],
            )}
            sprints={sprints.data?.sprints ?? []}
            epics={epics.data?.tasks ?? []}
            swimlanes={swimlanes}
            onClear={() => {
              // Clearing filters is not clearing display preferences.
              const kept = new URLSearchParams()
              if (view === "list") kept.set("view", view)
              if (swimlanes) kept.set("group", "epic")
              if (sort !== "created") kept.set("sort", sort)
              setParams(kept, { replace: true })
            }}
            activeCount={activeFilters.length}
          />
        </header>

        <ErrorNote error={loaded.error} />

        {/* The board fills this at h-full and scrolls itself; the list has no
            scroll of its own, so it scrolls here. */}
        <div className={`relative min-h-0 flex-1 ${view === "board" ? "overflow-hidden" : "overflow-y-auto overscroll-contain"}`}>
          {loaded.loading && !tasks.length ? (
            <Skeleton className="h-64" />
          ) : tasks.length ? (
            view === "board" ? (
              <BoardView tasks={shown} onMove={onMove} swimlanes={swimlanes} filterKey={filterKey} sortable={sort === "manual"} />
            ) : (
              <ListView tasks={shown} onMove={onMove} swimlanes={swimlanes} />
            )
          ) : activeFilters.length ? (
            <Empty title="No task matches these filters" hint="Clear them to see the whole project." />
          ) : (
            <Empty
              title="No tasks yet"
              hint="Create an epic or task with New item, or ask the agent to plan this work."
            />
          )}
        </div>

        {creating && (
          <TaskCreateModal
            projectId={projectId}
            employees={assignable}
            initialLevel={creating}
            onClose={() => setCreating(null)}
            onCreated={(task) => {
              loaded.reload()
              epics.reload()
              if (task.level === "epic") navigate(`/tasks/${task.id}`)
            }}
          />
        )}
      </div>
    </BoardConfigProvider>
  )
}

/** Removes the task, drops it back into the destination column at `index`, and
 *  leaves every other column's order untouched. The views read order by
 *  filtering on status, so only the position within the column matters. */
function applyMove(list: Task[], id: string, status: Status, index: number): Task[] {
  const moving = list.find((task) => task.id === id)
  if (!moving) return list

  const others = list.filter((task) => task.id !== id)
  const column = others.filter((task) => task.status === status)
  const rest = others.filter((task) => task.status !== status)

  column.splice(Math.max(0, Math.min(index, column.length)), 0, { ...moving, status })
  return [...rest, ...column]
}

function ProjectMenu({
  project,
  onChange,
  exportQuery,
}: {
  project: Project
  onChange: () => void
  exportQuery: URLSearchParams
}) {
  const toast = useToast()

  const navigate = useNavigate()
  const items: MenuItem[] = [
    // The per-project analytics page is gone; its hours report lives on the
    // company-wide Time Report, pre-filtered to this project.
    { label: "Time report", onSelect: () => navigate(`/time-report?projectIds=${project.id}`) },
    // Board settings are admin-gated server-side; the page says so to anyone
    // else rather than the menu guessing at roles.
    { label: "Board settings", onSelect: () => navigate(`/projects/${project.id}/settings`) },
    {
      label: "Export CSV",
      onSelect: async () => {
        // Through the API client so the request carries the auth header — a
        // bare <a href> would be an unauthenticated navigation and 401.
        // The board's current filters travel with it, so the file is the
        // board on screen rather than the whole project.
        const csv = await api.raw(`/projects/${project.id}/export.csv${exportQuery.size ? `?${exportQuery}` : ""}`)
        // The server sends a BOM so Excel reads the file as UTF-8, but
        // res.text() strips a leading BOM while decoding — put it back, or the
        // saved file arrives without it.
        const bom = csv.startsWith("\uFEFF") ? "" : "\uFEFF"
        const url = URL.createObjectURL(new Blob([bom, csv], { type: "text/csv" }))
        const anchor = document.createElement("a")
        anchor.href = url
        anchor.download = `${project.keyPrefix ?? "tasks"}.csv`
        anchor.click()
        URL.revokeObjectURL(url)
      },
    },
    project.archivedAt
      ? {
          label: "Restore project",
          onSelect: async () => {
            await api.post(`/projects/${project.id}/restore`, {})
            toast("Project restored")
            onChange()
          },
        }
      : {
          label: "Archive project",
          danger: true,
          onSelect: async () => {
            await api.post(`/projects/${project.id}/archive`, {})
            toast("Project archived")
            onChange()
          },
        },
  ]

  return (
    <Dropdown
      align="right"
      items={items}
      trigger={({ toggle }) => (
        <Button variant="ghost" size="sm" onClick={toggle}>
          Actions <ChevronDown />
        </Button>
      )}
    />
  )
}

// What the sort control shows; "Created" is the default the board opens with.
const SORT_LABELS: Record<BoardSort, string> = {
  created: "Created",
  priority: "Priority",
  manual: "Board order",
}

// The trigger's tooltip: the direction the short label leaves out.
const SORT_TITLES: Record<BoardSort, string> = {
  created: "Cards sorted by creation date, newest last",
  priority: "Cards sorted by priority, highest first",
  manual: "Cards in the order they were dragged into",
}

function Toolbar({
  params,
  setParam,
  view,
  sort,
  labelsInUse,
  assigneeOptions,
  sprints,
  epics,
  swimlanes,
  onClear,
  activeCount,
}: {
  params: URLSearchParams
  setParam: (key: string, value: string | null) => void
  view: "board" | "list"
  sort: BoardSort
  labelsInUse: string[]
  assigneeOptions: AssigneeFilterOption[]
  sprints: Sprint[]
  epics: Task[]
  swimlanes: boolean
  onClear: () => void
  activeCount: number
}) {
  const config = useBoardConfig()
  const [moreFilters, setMoreFilters] = useState(false)
  const advancedCount = ["level", "epicId", "priority", "type", "label", "includeArchived"].filter((key) => params.get(key)).length
  const filter = (key: string, label: string, options: { value: string; label: string; icon?: React.ReactNode; dividerBefore?: boolean }[]) => {
    const current = params.get(key)
    const chosen = options.find((o) => o.value === current)
    return (
      <Dropdown
        items={[
          // "Any status" rather than "All status": the singular noun is what the
          // button shows, and "All status" is not English.
          { label: `Any ${label.toLowerCase()}`, selected: !current, onSelect: () => setParam(key, null) },
          ...options.map((option) => ({
            label: (
              <span className="flex items-center gap-2">
                {option.icon}
                {option.label}
              </span>
            ),
            selected: current === option.value,
            onSelect: () => setParam(key, option.value),
            dividerBefore: option.dividerBefore,
          })),
        ]}
        trigger={({ toggle }) => (
          <Button variant={current ? "ghost" : "subtle"} size="sm" onClick={toggle}>
            {chosen?.label ?? label} <ChevronDown className="size-3" />
          </Button>
        )}
      />
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
      <label className="relative flex items-center">
        <Search className="pointer-events-none absolute left-2.5 size-3.5 text-faint" />
        <input
          type="search"
          defaultValue={params.get("q") ?? ""}
          placeholder="Search key or title"
          aria-label="Search tasks"
          // Debounced: a request per keystroke against a search that scans two
          // columns is wasteful, and the results flicker as they overtake.
          onChange={(event) => {
            const value = event.target.value
            window.clearTimeout(searchTimer)
            searchTimer = window.setTimeout(() => setParam("q", value.trim() || null), 250)
          }}
          className="w-full min-w-40 max-w-64 rounded-md border border-border bg-surface py-1.5 pr-2 pl-8 text-xs outline-none placeholder:text-muted focus:border-interactive"
        />
      </label>

      {filter(
        "status",
        "Status",
        config.statuses.map((s) => ({ value: s.id, label: s.name })),
      )}
      {filter("assigneeId", "Assignee", assigneeOptions)}
      {/* Not preselected on the current sprint: the URL stays the one place
          that says what the board is showing, and a pasted link means the
          same thing to whoever opens it. */}
      {sprints.length > 0 &&
        filter("sprintId", "Sprint", [
          { value: "active", label: "Current sprint" },
          { value: "none", label: "No sprint" },
          ...sprints
            .filter((sprint) => sprint.status !== "closed")
            .map((sprint) => ({ value: sprint.id, label: sprint.name })),
        ])}

      <Button variant={advancedCount ? "ghost" : "subtle"} size="sm" aria-expanded={moreFilters} aria-controls="board-more-filters" onClick={() => setMoreFilters((open) => !open)}>
        Filters{advancedCount > 0 ? ` (${advancedCount})` : ""} <ChevronDown className="size-3" />
      </Button>

      {activeCount > 0 && (
        <Button variant="subtle" size="sm" onClick={onClear}>
          Clear
        </Button>
      )}

      <div className="ml-auto flex items-center gap-2">
        {/* How the board is shown, apart from the filters that say what is in it. */}
        <Button
          variant={swimlanes ? "ghost" : "subtle"}
          size="sm"
          aria-pressed={swimlanes}
          title="Group the cards in one lane per epic"
          onClick={() => setParam("group", swimlanes ? null : "epic")}
        >
          Swimlanes
        </Button>

        {/* The board opens newest last; priority is only applied once it is
            picked, and Board order is how dragging comes back — cards cannot be
            dragged while a field sort is active. */}
        <Dropdown
          align="right"
          items={[
            { label: "Created · newest last", selected: sort === "created", onSelect: () => setParam("sort", null) },
            { label: "Priority · highest first", selected: sort === "priority", onSelect: () => setParam("sort", "priority") },
            { label: "Board order · drag to reorder", selected: sort === "manual", onSelect: () => setParam("sort", "manual") },
          ]}
          trigger={({ toggle }) => (
            <Button
              variant={sort === "created" ? "subtle" : "ghost"}
              size="sm"
              onClick={toggle}
              title={SORT_TITLES[sort]}
            >
              <SortIcon className="size-3.5" /> Sort: {SORT_LABELS[sort]} <ChevronDown className="size-3" />
            </Button>
          )}
        />

        <SegmentedControl
          ariaLabel="View"
          value={view}
          onChange={(next) => setParam("view", next === "board" ? null : next)}
          options={[
            { value: "board", label: <BoardIcon className="size-3.5" />, title: "Board" },
            { value: "list", label: <ListIcon className="size-3.5" />, title: "List" },
          ]}
        />
      </div>
      </div>
      {moreFilters && (
        <div id="board-more-filters" className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-2" aria-label="Additional board filters">
          {filter(
            "level",
            "Level",
            LEVELS.map((level) => ({ value: level, label: LEVEL_LABELS[level], icon: <LevelIcon level={level} /> })),
          )}
          {epics.length > 0 &&
            filter(
              "epicId",
              "Epic",
              epics.map((epic) => ({ value: epic.id, label: `${epic.key} ${epic.title}` })),
            )}
          {filter(
            "priority",
            "Priority",
            prioritiesOf(config).map((p) => ({
              value: p.id,
              label: p.name,
              icon: <PriorityIcon priority={p.id} />,
            })),
          )}
          {filter(
            "type",
            "Type",
            config.types.map((t) => ({ value: t.id, label: t.name, icon: <TypeIcon type={t.id} /> })),
          )}
          {labelsInUse.length > 0 &&
            filter(
              "label",
              "Label",
              labelsInUse.map((l) => ({ value: l, label: l })),
            )}

          <Button
            variant={params.get("includeArchived") ? "ghost" : "subtle"}
            size="sm"
            onClick={() => setParam("includeArchived", params.get("includeArchived") ? null : "true")}
          >
            Archived
          </Button>

        </div>
      )}
    </div>
  )
}

// Module-level rather than a ref: there is one search box on the page, and a
// ref would need threading through the filter helper for no benefit.
let searchTimer = 0
